import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    MediaLibraryDirentStatus,
    MediaLibraryDirentType,
    ROOT_DIRENT_ID,
    type MediaLibraryDirent,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/mediaLibraryWorkspaceCapability/domain/MediaLibraryDirent";
import {
    createMediaLibraryFolder,
    getMediaLibraryChildDirents,
    listAllMediaLibraryChildDirents,
    seedMediaLibraryRootDirent,
} from "~/modules/mediaLibrary/mediaLibraryApi";

export {ROOT_DIRENT_ID};

export const VARIANTS_MEDIA_LIBRARY_FOLDER_NAME = "variants";
export const MACHINES_MEDIA_LIBRARY_FOLDER_NAME = "machines";
export const MACHINE_TYPES_MEDIA_LIBRARY_FOLDER_NAME = "machine-types";
export const MANUFACTURERS_MEDIA_LIBRARY_FOLDER_NAME = "manufacturers";
export const PRODUCTS_MEDIA_LIBRARY_FOLDER_NAME = "products";
export const PRODUCT_CATEGORIES_MEDIA_LIBRARY_FOLDER_NAME = "product-categories";
export const PRODUCT_TYPES_MEDIA_LIBRARY_FOLDER_NAME = "product-types";
export const PRODUCT_BRANDS_MEDIA_LIBRARY_FOLDER_NAME = "product-brands";
/** Migrated website/Bragi brand logos live here (`/brands/effiplus-tires-logo.webp`). */
export const WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME = "brands";
export const COLLECTIONS_MEDIA_LIBRARY_FOLDER_NAME = "collections";

const MEDIA_LIBRARY_FOLDER_FILE_LIST_LIMIT = 1_000;

export function getBrandLogoMediaLibraryPathCandidates(slug: string): Array<string> {
    const normalized = slug.trim().toLowerCase();
    if (normalized.length === 0) {
        return [];
    }

    return [
        `${WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}-logo.webp`,
        `${WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}-logo-new-light.webp`,
        `${WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}-logo.png`,
        `${WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}.webp`,
        `${PRODUCT_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}-logo.webp`,
        `${PRODUCT_BRANDS_MEDIA_LIBRARY_FOLDER_NAME}/${normalized}.webp`,
    ];
}

const folderIdCache = new Map<string, Uuid>();

function folderCacheKey({
    folderName,
    parentFolderId,
    workspaceId,
}: {
    folderName: string;
    parentFolderId: Uuid;
    workspaceId: Uuid;
}): string {
    return `${workspaceId}:${parentFolderId}:${folderName}`;
}

export async function getMediaLibraryFolderId({
    accessToken,
    folderName,
    parentFolderId = ROOT_DIRENT_ID,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    folderName: string;
    parentFolderId?: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Uuid> {
    const cacheKey = folderCacheKey({
        folderName,
        parentFolderId,
        workspaceId,
    });
    const cachedFolderId = folderIdCache.get(cacheKey);
    if (cachedFolderId !== undefined) {
        return cachedFolderId;
    }

    const seedResult = await seedMediaLibraryRootDirent({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
    });
    if (seedResult.success !== true) {
        throw seedResult.error;
    }

    const childDirents = await listAllMediaLibraryChildDirents({
        accessToken,
        direntId: parentFolderId,
        intellsysBaseUrl,
        status: MediaLibraryDirentStatus.active,
        workspaceId,
    });

    const existingFolder = childDirents.find(
        (dirent) => dirent.type === MediaLibraryDirentType.folder && dirent.name === folderName,
    );
    if (existingFolder !== undefined) {
        folderIdCache.set(cacheKey, existingFolder.id);
        return existingFolder.id;
    }

    const createFolderResult = await createMediaLibraryFolder({
        accessToken,
        folderName,
        parentFolderId,
        intellsysBaseUrl,
        workspaceId,
    });
    if (createFolderResult.success !== true) {
        throw createFolderResult.error;
    }

    folderIdCache.set(cacheKey, createFolderResult.data.id);
    return createFolderResult.data.id;
}

export async function findExistingMediaLibraryFolderId({
    accessToken,
    folderName,
    parentFolderId = ROOT_DIRENT_ID,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    folderName: string;
    parentFolderId?: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Uuid | null> {
    const cacheKey = folderCacheKey({
        folderName: folderName,
        parentFolderId: parentFolderId,
        workspaceId: workspaceId,
    });
    const cachedFolderId = folderIdCache.get(cacheKey);
    if (cachedFolderId !== undefined) {
        return cachedFolderId;
    }

    const seedResult = await seedMediaLibraryRootDirent({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (seedResult.success !== true) {
        return null;
    }

    const childDirentsResult = await getMediaLibraryChildDirents({
        accessToken: accessToken,
        direntId: parentFolderId,
        intellsysBaseUrl: intellsysBaseUrl,
        status: MediaLibraryDirentStatus.active,
        type: MediaLibraryDirentType.folder,
        workspaceId: workspaceId,
    });
    if (childDirentsResult.success !== true) {
        return null;
    }

    const existingFolder = childDirentsResult.data.find((dirent) => {
        return dirent.type === MediaLibraryDirentType.folder && dirent.name === folderName;
    });
    if (existingFolder === undefined) {
        return null;
    }

    folderIdCache.set(cacheKey, existingFolder.id);
    return existingFolder.id;
}

export async function listMediaLibraryFolderFileDirents({
    accessToken,
    folderId,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    folderId: Uuid;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Array<MediaLibraryDirent>> {
    const childDirentsResult = await getMediaLibraryChildDirents({
        accessToken: accessToken,
        direntId: folderId,
        excludeFolders: true,
        intellsysBaseUrl: intellsysBaseUrl,
        limit: MEDIA_LIBRARY_FOLDER_FILE_LIST_LIMIT,
        status: MediaLibraryDirentStatus.active,
        workspaceId: workspaceId,
    });
    if (childDirentsResult.success !== true) {
        return [];
    }

    return childDirentsResult.data.filter((dirent) => {
        return dirent.type !== MediaLibraryDirentType.folder;
    });
}
